import { useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { Users, TrendUp } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Live Twitch follower count for itz_ool — proxied via backend → decapi.
// Polls every 45s; animates the number with a soft count-up on change.
export const FollowerCount = ({ className = "" }) => {
    const [count, setCount] = useState(null);
    const [loading, setLoading] = useState(true);
    const prevRef = useRef(0);
    const controls = useAnimation();

    useEffect(() => {
        let mounted = true;
        const fetchIt = async () => {
            try {
                const { data } = await axios.get(`${API}/twitch/follower-count`, { timeout: 8000 });
                if (mounted && typeof data?.followers === "number") {
                    prevRef.current = count ?? data.followers;
                    setCount(data.followers);
                }
            } catch {
                /* keep prior value */
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchIt();
        const id = setInterval(fetchIt, 45000);
        return () => {
            mounted = false;
            clearInterval(id);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (count === null) return;
        controls.start({
            scale: [1, 1.06, 1],
            transition: { duration: 0.6, ease: "easeOut" },
        });
    }, [count, controls]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`relative rounded-2xl border border-white/10 bg-gradient-to-r from-cyan-500/[0.08] via-sky-500/[0.05] to-purple-500/[0.06] backdrop-blur-md p-5 flex items-center gap-5 flex-wrap overflow-hidden ${className}`}
            data-testid="follower-count-card"
        >
            <div className="h-11 w-11 rounded-xl bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center shrink-0">
                <Users size={20} className="text-cyan-300" weight="fill" />
            </div>
            <div className="flex-1 min-w-[180px]">
                <div className="eyebrow text-cyan-300/80">Twitch followers · live count</div>
                <div className="flex items-baseline gap-3 mt-0.5">
                    <motion.span
                        animate={controls}
                        className="font-display text-3xl sm:text-4xl text-white font-semibold tabular-nums"
                        data-testid="follower-count-value"
                    >
                        {loading && count === null ? "…" : (count ?? 0).toLocaleString()}
                    </motion.span>
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-emerald-300/85 font-mono">
                        <TrendUp size={11} weight="bold" /> climbing
                    </span>
                </div>
                <div className="text-xs text-white/45 font-mono mt-1">
                    Road to 1,000 · then 10k · then top 50
                </div>
            </div>
        </motion.div>
    );
};
